import React from "react";
import { Mail, MessageCircle, BookOpen, ExternalLink, LifeBuoy } from "lucide-react";

export const SUPPORT_EMAIL = "flowhelp@yahoo.com";
const SUBJECT = "FlowFinder support request";

function mailtoHref(extra = "") {
  const params = new URLSearchParams({ subject: SUBJECT });
  if (extra) params.set("body", extra);
  return `mailto:${SUPPORT_EMAIL}?${params.toString()}`;
}

/**
 * Reusable support card. Drop it into modals, pages, or the settings menu.
 */
export default function SupportCard({ compact = false, className = "" }) {
  return (
    <div
      className={`card-surface relative overflow-hidden ${compact ? "p-5" : "p-6 sm:p-7"} ${className}`}
      data-testid="support-card"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.18), transparent 55%)" }}
      />
      <div className="relative">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center" style={{ boxShadow: "0 0 24px rgba(0,255,156,0.22)" }}>
            <LifeBuoy className="w-4 h-4 text-[var(--bull)]" />
          </div>
          <div className="label-mono">Support</div>
        </div>
        <h3 className={`text-white font-semibold tracking-tight ${compact ? "text-xl" : "text-2xl sm:text-3xl"}`}>
          Need help?
        </h3>
        <p className="text-white/55 mt-2 text-sm leading-relaxed">
          Questions, bugs, feedback, or account issues — we're a single email away.
        </p>

        <div className="mt-5 flex items-center justify-between gap-3 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Mail className="w-3.5 h-3.5 text-white/70 shrink-0" />
            <a
              href={mailtoHref()}
              className="font-mono text-[13px] text-white hover:text-[var(--bull)] transition-colors truncate"
              data-testid="support-email-link"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
          <button
            onClick={() => navigator.clipboard?.writeText(SUPPORT_EMAIL)}
            className="text-[10px] tracking-widest uppercase font-mono text-white/40 hover:text-white transition-colors shrink-0"
            data-testid="support-copy-email"
          >
            Copy
          </button>
        </div>

        <div className={`mt-5 flex flex-wrap gap-2 ${compact ? "" : "sm:gap-3"}`}>
          <a
            href={mailtoHref("Hi FlowFinder team,\n\n")}
            className="btn-accent flex items-center gap-2"
            data-testid="support-contact-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Contact Support
          </a>
          <a
            href={mailtoHref("Hi FlowFinder team,\n\nI'd like to report a bug:\n\nWhat happened:\nWhat I expected:\nSteps to reproduce:\n")}
            className="btn-ghost flex items-center gap-2"
            data-testid="support-bug-btn"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Report a bug
          </a>
        </div>

        {!compact && (
          <div className="mt-6 pt-5 border-t border-white/5 grid sm:grid-cols-3 gap-3 text-sm">
            <Tip label="Response time" value="Within 24 hours" />
            <Tip label="Coverage" value="Mon-Fri, 9-6 ET" />
            <Tip label="Languages" value="English" />
          </div>
        )}
      </div>
    </div>
  );
}

function Tip({ label, value }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className="text-white text-[13px] mt-1">{value}</div>
    </div>
  );
}

/** Compact text used in auth/footer areas. */
export function SupportInline({ className = "" }) {
  return (
    <div className={`text-center text-xs text-white/45 ${className}`} data-testid="support-inline">
      Need help? Contact{" "}
      <a
        href={mailtoHref()}
        className="text-white/80 hover:text-[var(--bull)] underline underline-offset-2 decoration-white/20"
        data-testid="support-inline-link"
      >
        {SUPPORT_EMAIL}
      </a>
    </div>
  );
}
